/**
 * The catalog registry: English, plus whatever each language pack has translated so far.
 *
 * Namespaces are split by *screen family*, not by component, so a language pack can ship a
 * screen at a time (`catalogs/en/index.ts` is the list, and the only list).
 *
 * **Fallback is per key, not per namespace.** A pack that has translated nine of a screen's
 * twelve messages renders nine of them; the other three come out in English. The alternative
 * — resolving a whole namespace to English because one key is missing — makes the first
 * translated message worthless and the last one a cliff, which is how a translation stalls
 * at 90% forever.
 *
 * That has a consequence the formatter must respect: a resolved message carries **the
 * language it is actually written in**, because ICU plural and select rules belong to that
 * language. Formatting an English plural under Slovak rules picks the wrong branch, and
 * Slovak has three of them where English has two.
 */

import { enCatalogs } from './catalogs/en';
import type { LanguagePack, MessageKeys, Namespace } from './catalogs/pack';
import type { Locale } from './locales';
import { FALLBACK_LOCALE, SUPPORTED_LOCALES } from './locales';

export type { Catalog, MessageKeys, Namespace } from './catalogs/pack';

/**
 * How each translation arrives: **by `import()`, when its language is asked for.** English is
 * deliberately absent: it is not one pack among several, it is the base every other pack is an
 * overlay on, and the type system already treats it that way (`catalogs/pack.ts`) — so it is
 * the one catalog that is imported statically and is always here.
 *
 * The packs used to be static imports too, which put every language in the main bundle: a
 * German reader downloaded Slovak and a Slovak reader downloaded German, on the sign-in page,
 * before either had typed a character (review 2026-09-25 §3.22). A dynamic import is one
 * chunk per language, fetched once per browser and cached by its content hash from then on.
 *
 * TWIN: `catalogs/parity.test.ts` lists the same packs to check them against English; a
 * language added there is added here, or it is checked and never shipped.
 */
const LOADERS: Partial<Readonly<Record<Locale, () => Promise<LanguagePack>>>> = {
  de: async () => (await import('./catalogs/de')).deCatalogs,
  sk: async () => (await import('./catalogs/sk')).skCatalogs,
};

/** The packs that have arrived. Read synchronously by every resolver below. */
const loaded = new Map<Locale, LanguagePack>();
/** The packs in flight, so two screens asking for one language share one request. */
const loading = new Map<Locale, Promise<void>>();

/**
 * Pack arrival, as an external store for `useSyncExternalStore`.
 *
 * A component that resolved a catalog before its pack landed is holding the English fallback,
 * and nothing in its props changes when the translation arrives. The version does, and the
 * hooks in `useMessages.ts` read it, which is what re-renders them in the right language.
 */
let version = 0;
const listeners = new Set<() => void>();

export function subscribePacks(listener: () => void): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

export function packVersion(): number {
  return version;
}

/**
 * Whether resolving in this language gives the real answer right now.
 *
 * `true` for English, for a language with no pack at all (it has nothing to load and falls back
 * per key as it always did), and for a pack that has arrived. `false` means `resolveCatalog`
 * would hand back English for every key, and a caller that must not show that waits on
 * `loadPack` first — `app/main.tsx` before the first render, `store.ts` before a switch.
 */
export function isPackReady(locale: Locale): boolean {
  return LOADERS[locale] === undefined || loaded.has(locale);
}

/**
 * Fetch a language's pack, once. Idempotent and safe to call from anywhere a language is
 * about to be needed; a rejected fetch is forgotten so the next call can try again, and the
 * caller decides whether English in the meantime is acceptable.
 */
export function loadPack(locale: Locale): Promise<void> {
  const load = LOADERS[locale];
  if (load === undefined || loaded.has(locale)) return Promise.resolve();
  const pending = loading.get(locale);
  if (pending !== undefined) return pending;
  const started = load()
    .then((pack) => {
      loaded.set(locale, pack);
      version += 1;
      for (const listener of listeners) listener();
    })
    .finally(() => {
      loading.delete(locale);
    });
  loading.set(locale, started);
  return started;
}

/** One message, and the language it turned out to be written in. */
export interface ResolvedMessage {
  readonly pattern: string;
  readonly locale: Locale;
}

export type ResolvedCatalog<N extends Namespace> = Readonly<
  Record<MessageKeys[N], ResolvedMessage>
>;

/**
 * Merging is per (namespace, locale) and the result never changes during a session, so it is
 * computed once. A screen with sixty keys otherwise rebuilds the same object on every render
 * of every component that reads it.
 *
 * Only a **ready** language is cached: resolving before its pack lands yields English, which
 * is a correct answer for that moment and a wrong one to remember — the version bump on
 * arrival re-runs the callers, and this must then compute the real merge rather than replay
 * the fallback.
 */
const merged = new Map<string, ResolvedCatalog<Namespace>>();

/** The catalog for a namespace in the requested language, English where it has no answer. */
export function resolveCatalog<N extends Namespace>(
  namespace: N,
  locale: Locale,
): ResolvedCatalog<N> {
  const ready = isPackReady(locale);
  const cacheKey = `${locale} ${namespace}`;
  const cached = ready ? merged.get(cacheKey) : undefined;
  if (cached !== undefined) return cached;

  const base: Readonly<Record<string, string>> = enCatalogs[namespace];
  const overlay: Readonly<Record<string, string | undefined>> =
    loaded.get(locale)?.[namespace] ?? {};

  const resolved: Record<string, ResolvedMessage> = {};
  for (const key of Object.keys(base)) {
    const translated = overlay[key];
    const english = base[key] ?? key;
    // An empty string is a key a translator opened and did not finish. It renders as nothing,
    // which reads as a broken screen rather than an untranslated one — treat it as missing.
    resolved[key] =
      translated === undefined || translated === ''
        ? { pattern: english, locale: FALLBACK_LOCALE }
        : { pattern: translated, locale };
  }

  const catalog = resolved as ResolvedCatalog<N>;
  if (ready) merged.set(cacheKey, catalog as ResolvedCatalog<Namespace>);
  return catalog;
}

/**
 * The keys of a namespace this language has not translated yet. Feeds the coverage report.
 * Answers for the pack as loaded — `await loadPack(locale)` first, or every key is missing.
 */
export function missingMessageKeys(namespace: Namespace, locale: Locale): readonly string[] {
  if (locale === FALLBACK_LOCALE) return [];
  const overlay: Readonly<Record<string, string | undefined>> =
    loaded.get(locale)?.[namespace] ?? {};
  return Object.keys(enCatalogs[namespace]).filter((key) => {
    const translated = overlay[key];
    return translated === undefined || translated === '';
  });
}

/**
 * Which languages have at least one real message in this namespace — the rest fall back.
 * Same caveat as `missingMessageKeys`: a pack not yet loaded reads as untranslated.
 */
export function translatedLocales(namespace: Namespace): readonly Locale[] {
  return SUPPORTED_LOCALES.filter((locale) => {
    if (locale === FALLBACK_LOCALE) return true;
    return Object.keys(loaded.get(locale)?.[namespace] ?? {}).length > 0;
  });
}
